import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setTitle } from '../../../store/reducer/appReducer';
import api from '../../../plugins/axios';

interface PasswordPolicyConfig {
  passwordExpiryDays: number;
  minPasswordLength: number;
  requireSpecialChar?: boolean;
}

interface PasswordPolicyConfigResponse {
  message: string;
  data: {
    id: number;
    passwordExpiryDays: number;
    minPasswordLength: number;
    requireSpecialChar: boolean;
  };
}

const passwordPolicyConfigService = {
  getConfig: (): Promise<{data: PasswordPolicyConfigResponse}> => {
    return api.get('/admin/password-policy-config');
  },
  updateConfig: (config: PasswordPolicyConfig): Promise<{ message: string }> => {
    const payload = {
      passwordExpiryDays: config.passwordExpiryDays,
      minPasswordLength: config.minPasswordLength,
      requireSpecialChar: true // Always require special char for now
    };
    return api.put('/admin/password-policy-config', payload);
  },
};

const PasswordPolicySettings: React.FC = () => {
  const dispatch = useDispatch();
  const [config, setConfig] = useState<PasswordPolicyConfig>({
    passwordExpiryDays: 200,
    minPasswordLength: 12,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    dispatch(setTitle('ตั้งค่านโยบายรหัสผ่าน'));
    fetchConfig();
  }, [dispatch]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      console.log('Calling GET API...'); // Debug log
      const response = await passwordPolicyConfigService.getConfig();
      console.log('GET API response:', response); // Debug log
      if (response.data) {
        const apiData = response.data.data;
        const newConfig = {
          passwordExpiryDays: apiData.passwordExpiryDays ?? 200,
          minPasswordLength: apiData.minPasswordLength ?? 12,
        };
        console.log('Setting config to:', newConfig); // Debug log
        setConfig(newConfig);
      }
    } catch (error: any) {
      console.error('GET API error:', error); // Debug log
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
      });
    }
    setLoading(false);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (config.passwordExpiryDays < 1 || config.passwordExpiryDays > 365) {
      newErrors.passwordExpiryDays = 'จำนวนวันหมดอายุต้องอยู่ระหว่าง 1-365 วัน';
    }

    if (config.minPasswordLength < 6 || config.minPasswordLength > 50) {
      newErrors.minPasswordLength = 'ความยาวรหัสผ่านขั้นต่ำต้องอยู่ระหว่าง 6-50 ตัวอักษร';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await passwordPolicyConfigService.updateConfig(config);
      setMessage({
        type: 'success',
        text: 'บันทึกการตั้งค่าเรียบร้อยแล้ว',
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึก',
      });
    }
    setSaving(false);
  };

  const handleInputChange = (field: keyof PasswordPolicyConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    const value = inputValue === '' ? 0 : parseInt(inputValue) || 0;
    setConfig((prev: PasswordPolicyConfig) => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: { [key: string]: string }) => ({ ...prev, [field]: '' }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" component="h1">
              ตั้งค่านโยบายรหัสผ่าน
            </Typography>
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ mb: 3 }}>
              {message.text}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="จำนวนวันหมดอายุรหัสผ่าน"
                type="number"
                value={config.passwordExpiryDays || ''}
                onChange={handleInputChange('passwordExpiryDays')}
                error={!!errors.passwordExpiryDays}
                helperText={
                  errors.passwordExpiryDays || 
                  'กำหนดจำนวนวันที่รหัสผ่านจะหมดอายุ (1-365 วัน)'
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">วัน</InputAdornment>,
                }}
                inputProps={{
                  min: 1,
                  max: 365,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ความยาวรหัสผ่านขั้นต่ำ"
                type="number"
                value={config.minPasswordLength || ''}
                onChange={handleInputChange('minPasswordLength')}
                error={!!errors.minPasswordLength}
                helperText={
                  errors.minPasswordLength || 
                  'กำหนดความยาวขั้นต่ำของรหัสผ่าน (6-50 ตัวอักษร)'
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">ตัวอักษร</InputAdornment>,
                }}
                inputProps={{
                  min: 6,
                  max: 50,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  คำแนะนำการตั้งค่า:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • จำนวนวันหมดอายุรหัสผ่าน: ควรตั้งไว้ระหว่าง 90-365 วัน เพื่อความปลอดภัย
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • ความยาวรหัสผ่านขั้นต่ำ: ควรตั้งไว้อย่างน้อย 8-12 ตัวอักษร เพื่อป้องกันการถูกเดา
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • การเปลี่ยนแปลงจะมีผลกับผู้ใช้ทั้งหมดในระบบ
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordPolicySettings;
